import * as React from 'react';
import { compose, withState, withHandlers, setDisplayName, withProps } from 'recompose';
import { connect } from 'react-redux';
import * as Moment from 'moment';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { UtcTime } from 'types';
import { setDate } from 'actions/monitoringActions';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { State } from 'reducers';
import { getDate } from 'selectors/monitoringSelectors';
import { FadeScale } from 'ui/transitions';
import { Section } from 'ui/shared/Section';
import { DatePicker } from './DatePicker';
import { DownTriangle } from 'ui/shapes';
import { startOfTodayOnServer } from 'logic/timezones';

import './DateSection.css';

const RawEmbeddedDatePicker = ({
    serverDate,
    serverToday,
    tzOffsetCalculator,
    onAccept,
    open,
    toggleOpen,
    styles,
}: StateProps & RecomposeProps & OemProps) =>
    <div style={ styles.inline }>
        <span
            style={ styles.field }
            onClick={ toggleOpen }
        >
            <span style={ styles.fieldContent }>
                { Moment(serverDate).format('l') }
            </span>
            <DownTriangle
                width=".5em"
                height=".6em"
                style={ styles.downArrow }
            />
        </span>
        <div style={ styles.dropDownHost }>
            <FadeScale
                visible={ open }
                duration={ 100 }
                from={ .1 }
                to={ 1 }
                style={ styles.fadeScale }
                unmountOnExit={ true }
            >
                <DatePicker
                    value={ serverDate }
                    today={ serverToday }
                    onAccept={ onAccept }
                    onClose={ toggleOpen }
                    tzOffsetCalculator={ tzOffsetCalculator }
                />
            </FadeScale>
        </div>
    </div>
;

type RecomposeProps = {
    open: boolean;
    serverToday: UtcTime; // Start of today in server's timezone
    toggleOpen: () => void;
    onAccept: (date: Date) => void;
    setOpen: (o: boolean | ((b: boolean) => boolean)) => void;
    styles: { [name: string]: React.CSSProperties };
};
type StateProps = {
    serverDate: Date;
    tzOffsetCalculator: (t: UtcTime) => number;
};
type DispProps = {
    onChange: (date: Date) => void;
};

const mapStateToProps = (state: State): StateProps => ({
    serverDate: getDate(state),
    tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

const mapDispatchToProps = (dispatch: Function): DispProps => ({
    onChange: date => dispatch(setDate({ date, preserveTimeOfDay: true })),
});

const EmbeddedDatePicker = compose<StateProps & RecomposeProps & OemProps, {}>(
    setDisplayName('EmbeddedDatePicker'),
    skin,
    connect(mapStateToProps, mapDispatchToProps),
    withProps(({ tzOffsetCalculator }) => ({
        serverToday: startOfTodayOnServer(tzOffsetCalculator),
    })),
    withState('open', 'setOpen', false),
    withHandlers<StateProps & DispProps & RecomposeProps & OemProps, Partial<RecomposeProps>>({
        toggleOpen: ({ setOpen }) => () => setOpen((open: boolean) => !open),
        onAccept: ({ setOpen, onChange }) => (value: Date) => {
            setOpen(false);
            onChange(value);
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            inline: {
                display: 'inline',
            },
            field: {
                ...styles.Bordered,
                ...styles.Input,
                padding: '3px 6px',
                cursor: 'pointer',
            },
            fieldContent: {
                fontSize: '.8em',
            },
            downArrow: {
                fill: '#fff',
                marginLeft: '1em',
            },
            dropDownHost: {
                position: 'relative',
                left: '5em',
            },
            fadeScale: {
                zIndex: 999,
                position: 'fixed',
                transformOrigin: '25% 0% 0',
            },
        },
    })),
)(RawEmbeddedDatePicker);


const RawDateSection = ({ t, styles }: TranslateProps & OemProps) =>
    <Section
        id="date-section"
        titleKey="927"
        embed={ () => <EmbeddedDatePicker/> }
    />
;

export const DateSection = compose<TranslateProps & OemProps, {}>(
    setDisplayName('DateSection'),
    translate(),
    skin,
)(RawDateSection);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/sidePanel/DateSection.tsx